import React, { useState, useEffect } from "react";
import { contentAdminAPI } from "../../api/apiService";
import { FileText, Save, RefreshCw, ChevronRight, Layout, Trash2, Plus, Info } from "lucide-react";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import toast from "react-hot-toast";

const ContentManagement = () => {
    const [pages, setPages] = useState([]);
    const [selectedPage, setSelectedPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({ title: "", slug: "", content: "", is_published: true });
    const [isNewPage, setIsNewPage] = useState(false);

    const PREDEFINED_PAGES = [
        { title: "Terms of Service", slug: "terms-of-service" },
        { title: "Privacy Policy", slug: "privacy-policy" },
        { title: "Contact Us", slug: "contact-us" },
        { title: "Help Center", slug: "help-center" }
    ];

    useEffect(() => {
        fetchPages();
    }, []);

    const fetchPages = async () => {
        try {
            setLoading(true);
            const res = await contentAdminAPI.getAllPages();
            setPages(res.data);
            
            // If we have pages, select the first one by default if none selected
            if (res.data.length > 0 && !selectedPage && !isNewPage) {
                handleSelectPage(res.data[0]);
            }
        } catch (error) {
            toast.error("Failed to fetch content pages");
        } finally {
            setLoading(false);
        }
    };

    const handleSelectPage = (page) => {
        setIsNewPage(false);
        setSelectedPage(page);
        setFormData({
            title: page.title,
            slug: page.slug,
            content: page.content || "",
            is_published: page.is_published
        });
    };

    const handleCreateNew = () => {
        setIsNewPage(true);
        setSelectedPage(null);
        setFormData({ title: "", slug: "", content: "", is_published: true });
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        let processedValue = type === 'checkbox' ? checked : value;
        
        // Strictly sanitize slug input: remove URL parts and invalid characters
        if (name === 'slug' && typeof processedValue === 'string') {
            processedValue = processedValue
                .toLowerCase()
                .replace(/^https?:\/\/[^\/]+\//, '') // Remove domain part if pasted as URL
                .replace(/[^a-z0-9-_]/g, '-')         // Replace invalid characters with hyphens
                .replace(/-+/g, '-')                 // Replace multiple hyphens with one
                .replace(/^-+|-+$/g, '');            // Trim hyphens from ends
        }

        setFormData(prev => {
            const newData = { ...prev, [name]: processedValue };
            
            // Auto-generate slug from title if it's a new page and slug wasn't manually edited or is empty
            if (isNewPage && name === 'title' && (!prev.slug || prev.slug === prev.title.toLowerCase().replace(/[^a-z0-9]/g, '-'))) {
                newData.slug = processedValue
                    .toLowerCase()
                    .replace(/[^a-z0-9]/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-+|-+$/g, '');
            }
            
            return newData;
        });
    };

    const handleEditorChange = (content) => {
        setFormData(prev => ({ ...prev, content }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.slug) {
            toast.error("Title and Slug are required");
            return;
        }

        try {
            setIsSaving(true);
            if (isNewPage) {
                await contentAdminAPI.createPage(formData);
                toast.success("Page created successfully");
                setIsNewPage(false);
            } else {
                await contentAdminAPI.updatePage(formData.slug, formData);
                toast.success("Page updated successfully");
            }
            fetchPages();
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to save page");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedPage) return;
        if (!window.confirm(`Are you sure you want to delete "${selectedPage.title}"?`)) return;

        try {
            setLoading(true);
            await contentAdminAPI.deletePage(selectedPage.slug);
            toast.success("Page deleted successfully");
            setSelectedPage(null);
            fetchPages();
        } catch (error) {
            toast.error("Failed to delete page");
        } finally {
            setLoading(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'color': [] }, { 'background': [] }],
            ['link', 'image'],
            ['clean']
        ],
    };

    return (
        <div className="flex flex-col lg:flex-row gap-6 h-full min-h-[700px]">
            {/* Sidebar: List of Pages */}
            <div className="w-full lg:w-80 flex flex-col gap-4">
                <div className="bg-white dark:bg-slate-800 rounded-2xl p-4 border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4 px-2">
                        <h3 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <Layout className="w-4 h-4 text-indigo-500" />
                            Contacts
                        </h3>
                        <button 
                            onClick={handleCreateNew}
                            className="p-1.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 transition-colors"
                            title="Create New Page"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="space-y-1 overflow-y-auto max-h-[500px] pr-2 custom-scrollbar">
                        {loading && pages.length === 0 ? (
                            <div className="flex justify-center py-8">
                                <RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" />
                            </div>
                        ) : pages.length === 0 ? (
                            <div className="text-center py-8 px-4">
                                <p className="text-xs text-slate-500">No dynamic pages found.</p>
                                <button 
                                    onClick={handleCreateNew}
                                    className="text-xs text-indigo-500 font-bold mt-2 hover:underline"
                                >
                                    Create First Page
                                </button>
                            </div>
                        ) : (
                            pages.map((page) => (
                                <button
                                    key={page.slug}
                                    onClick={() => handleSelectPage(page)}
                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                                        selectedPage?.slug === page.slug && !isNewPage
                                            ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/20"
                                            : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                                    }`}
                                >
                                    <div className="flex items-center gap-3 overflow-hidden">
                                        <FileText className={`w-4 h-4 flex-shrink-0 ${selectedPage?.slug === page.slug && !isNewPage ? "text-white" : "text-indigo-500"}`} />
                                        <span className="text-sm font-bold truncate">{page.title}</span>
                                    </div>
                                    <ChevronRight className={`w-4 h-4 flex-shrink-0 ${selectedPage?.slug === page.slug && !isNewPage ? "text-white/50" : "text-slate-300"}`} />
                                </button>
                            ))
                        )}
                    </div>

                    <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700/50 px-2">
                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-3 border border-amber-100 dark:border-amber-900/30">
                            <div className="flex gap-2 text-amber-700 dark:text-amber-400">
                                <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <p className="text-[10px] leading-relaxed">
                                    <strong>Tip:</strong> Use standard slugs for core pages to ensure they appear in the website footer.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content: Editor */}
            <div className="flex-1">
                {(selectedPage || isNewPage) ? (
                    <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-xl overflow-hidden flex flex-col h-full">
                        <div className="px-8 py-6 border-b border-slate-100 dark:border-slate-700/50 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-50/50 dark:bg-slate-800/50">
                            <div>
                                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                                    {isNewPage ? "Create Content Page" : `Editing: ${selectedPage.title}`}
                                </h3>
                                <p className="text-xs text-slate-500 mt-1">
                                    Last updated: {selectedPage?.updated_at ? new Date(selectedPage.updated_at).toLocaleString() : 'Never'}
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                {!isNewPage && (
                                    <button
                                        type="button"
                                        onClick={handleDelete}
                                        className="p-2.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors border border-transparent hover:border-red-100 dark:hover:border-red-900/30"
                                        title="Delete Page"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all disabled:opacity-50"
                                >
                                    {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    {isSaving ? "Saving..." : "Save Page"}
                                </button>
                            </div>
                        </div>

                        <div className="p-8 space-y-6 flex-1 overflow-y-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Page Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="e.g. Terms of Service"
                                        className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Page Slug (URL)</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            name="slug"
                                            value={formData.slug}
                                            onChange={handleInputChange}
                                            placeholder="e.g. terms-of-service"
                                            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none transition-all pr-12"
                                            required
                                            disabled={!isNewPage && PREDEFINED_PAGES.some(p => p.slug === formData.slug)}
                                        />
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                            <Layout className="w-4 h-4 text-slate-300" />
                                        </div>
                                    </div>
                                    <p className="text-[10px] text-slate-500 ml-1">Unique path used in the website URL.</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-2 px-1">
                                <input
                                    type="checkbox"
                                    id="is_published"
                                    name="is_published"
                                    checked={formData.is_published}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                />
                                <label htmlFor="is_published" className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                    Published (Visible to customers)
                                </label>
                            </div>

                            <div className="space-y-2 flex-1 flex flex-col min-h-[400px]">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest ml-1">Page Content</label>
                                <div className="flex-1 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden bg-white dark:bg-white text-slate-900">
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.content}
                                        onChange={handleEditorChange}
                                        modules={quillModules}
                                        className="h-full border-none"
                                        placeholder="Start writing your page content here..."
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                ) : (
                    <div className="h-full bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col items-center justify-center p-12 text-center border-dashed">
                        <div className="w-20 h-20 bg-indigo-50 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mb-6">
                            <FileText className="w-10 h-10 text-indigo-500" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Page Selected</h3>
                        <p className="text-slate-500 dark:text-slate-400 max-w-sm">
                            Select a content page from the sidebar to edit it, or create a new one to add more informational sections to your website.
                        </p>
                        <button 
                            onClick={handleCreateNew}
                            className="mt-8 px-8 py-3 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-500/20 hover:bg-indigo-700 transition-all flex items-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Create New Page
                        </button>
                    </div>
                )}
            </div>
            
            <style>{`
                .ql-container {
                    font-family: 'Inter', sans-serif !important;
                    font-size: 16px !important;
                    min-height: 350px !important;
                }
                .ql-editor {
                    min-height: 350px !important;
                }
                .ql-toolbar {
                    border: none !important;
                    border-bottom: 1px solid #e2e8f0 !important;
                    background: #f8fafc !important;
                }
                .dark .ql-toolbar {
                    background: #1e293b !important;
                    border-bottom: 1px solid #334155 !important;
                }
                .dark .ql-snow .ql-stroke {
                    stroke: #94a3b8 !important;
                }
                .dark .ql-snow .ql-fill {
                    fill: #94a3b8 !important;
                }
                .dark .ql-snow .ql-picker {
                    color: #94a3b8 !important;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #e2e8f0;
                    border-radius: 10px;
                }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #334155;
                }
            `}</style>
        </div>
    );
};

export default ContentManagement;
